import { useState, useMemo } from 'react';
import { cn } from '../utils/cn';
import {
  Bell,
  BellRing,
  Plus,
  X,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Volume2,
  Target,
  Zap,
  Clock,
  Check,
  Trash2,
  Edit,
  ChevronDown,
  Filter,
  AlertTriangle,
  Mail,
  Smartphone,
  Globe,
  Play,
  Pause,
} from 'lucide-react';

interface Alert {
  id: string;
  name: string;
  symbol: string;
  condition: AlertCondition;
  status: 'active' | 'triggered' | 'paused' | 'expired';
  createdAt: number;
  triggeredAt?: number;
  expiresAt?: number;
  notifications: AlertNotification[];
  triggerCount: number;
  maxTriggers?: number;
}

interface AlertCondition {
  type: 'price_above' | 'price_below' | 'price_cross' | 'percent_change' | 'volume_spike' | 'rsi_above' | 'rsi_below' | 'macd_cross';
  value: number;
  value2?: number; // For ranges
  timeframe?: string;
}

interface AlertNotification {
  type: 'app' | 'email' | 'telegram' | 'webhook';
  enabled: boolean;
  destination?: string;
}

const ALERT_TYPES = [
  { id: 'price_above', name: 'Price Above', icon: TrendingUp, color: '#00D4AA', description: 'Triggers when price goes above target' },
  { id: 'price_below', name: 'Price Below', icon: TrendingDown, color: '#FF6B6B', description: 'Triggers when price goes below target' },
  { id: 'price_cross', name: 'Price Crosses', icon: Target, color: '#3B82F6', description: 'Triggers when price crosses target in either direction' },
  { id: 'percent_change', name: '% Change', icon: Activity, color: '#8B5CF6', description: 'Triggers on percentage move within timeframe' },
  { id: 'volume_spike', name: 'Volume Spike', icon: Volume2, color: '#F59E0B', description: 'Triggers on unusual volume activity' },
  { id: 'rsi_above', name: 'RSI Above', icon: Zap, color: '#EF4444', description: 'Triggers when RSI exceeds threshold' },
  { id: 'rsi_below', name: 'RSI Below', icon: Zap, color: '#10B981', description: 'Triggers when RSI falls below threshold' },
  { id: 'macd_cross', name: 'MACD Cross', icon: Activity, color: '#EC4899', description: 'Triggers on MACD crossover signal' },
];

const MOCK_ALERTS: Alert[] = [
  {
    id: '1',
    name: 'BTC Breakout Alert',
    symbol: 'BTC-USDT',
    condition: { type: 'price_above', value: 100000 },
    status: 'active',
    createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
    notifications: [
      { type: 'app', enabled: true },
      { type: 'email', enabled: true, destination: 'user@email.com' },
    ],
    triggerCount: 0,
    maxTriggers: 3,
  },
  {
    id: '2',
    name: 'ETH Support Watch',
    symbol: 'ETH-USDT',
    condition: { type: 'price_below', value: 3200 },
    status: 'active',
    createdAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
    notifications: [
      { type: 'app', enabled: true },
      { type: 'telegram', enabled: true },
    ],
    triggerCount: 0,
  },
  {
    id: '3',
    name: 'SOL RSI Oversold',
    symbol: 'SOL-USDT',
    condition: { type: 'rsi_below', value: 30 },
    status: 'triggered',
    createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
    triggeredAt: Date.now() - 1 * 60 * 60 * 1000,
    notifications: [{ type: 'app', enabled: true }],
    triggerCount: 1,
    maxTriggers: 1,
  },
  {
    id: '4',
    name: 'LINK Volume Alert',
    symbol: 'LINK-USDT',
    condition: { type: 'volume_spike', value: 200 },
    status: 'paused',
    createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
    notifications: [{ type: 'app', enabled: true }],
    triggerCount: 5,
  },
];

const ASSETS = ['BTC-USDT', 'ETH-USDT', 'SOL-USDT', 'BNB-USDT', 'XRP-USDT', 'DOGE-USDT', 'ADA-USDT', 'AVAX-USDT', 'LINK-USDT', 'DOT-USDT'];

export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>(MOCK_ALERTS);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'triggered' | 'paused'>('all');
  const [filterSymbol, setFilterSymbol] = useState<string>('all');

  // Create alert form state
  const [newAlert, setNewAlert] = useState({
    name: '',
    symbol: 'BTC-USDT',
    conditionType: 'price_above' as AlertCondition['type'],
    value: '',
    value2: '',
    timeframe: '1h',
    notifications: {
      app: true,
      email: false,
      telegram: false,
      webhook: false,
    },
    emailAddress: '',
    webhookUrl: '',
    maxTriggers: '',
    expireAfter: '',
  });

  const filteredAlerts = useMemo(() => {
    return alerts.filter(alert => {
      if (filterStatus !== 'all' && alert.status !== filterStatus) return false;
      if (filterSymbol !== 'all' && alert.symbol !== filterSymbol) return false;
      return true;
    });
  }, [alerts, filterStatus, filterSymbol]);

  const stats = useMemo(() => ({
    total: alerts.length,
    active: alerts.filter(a => a.status === 'active').length,
    triggered: alerts.filter(a => a.status === 'triggered').length,
    paused: alerts.filter(a => a.status === 'paused').length,
  }), [alerts]);

  const handleCreateAlert = () => {
    const alert: Alert = {
      id: Date.now().toString(),
      name: newAlert.name || `${newAlert.symbol} Alert`,
      symbol: newAlert.symbol,
      condition: {
        type: newAlert.conditionType,
        value: parseFloat(newAlert.value),
        value2: newAlert.value2 ? parseFloat(newAlert.value2) : undefined,
        timeframe: newAlert.timeframe,
      },
      status: 'active',
      createdAt: Date.now(),
      notifications: [
        { type: 'app' as const, enabled: newAlert.notifications.app },
        { type: 'email' as const, enabled: newAlert.notifications.email, destination: newAlert.emailAddress },
        { type: 'telegram' as const, enabled: newAlert.notifications.telegram },
        { type: 'webhook' as const, enabled: newAlert.notifications.webhook, destination: newAlert.webhookUrl },
      ].filter(n => n.enabled) as AlertNotification[],
      triggerCount: 0,
      maxTriggers: newAlert.maxTriggers ? parseInt(newAlert.maxTriggers) : undefined,
      expiresAt: newAlert.expireAfter ? Date.now() + parseInt(newAlert.expireAfter) * 24 * 60 * 60 * 1000 : undefined,
    };

    setAlerts([alert, ...alerts]);
    setShowCreateModal(false);
    setNewAlert({
      name: '',
      symbol: 'BTC-USDT',
      conditionType: 'price_above',
      value: '',
      value2: '',
      timeframe: '1h',
      notifications: { app: true, email: false, telegram: false, webhook: false },
      emailAddress: '',
      webhookUrl: '',
      maxTriggers: '',
      expireAfter: '',
    });
  };

  const toggleAlertStatus = (id: string) => {
    setAlerts(alerts.map(alert => {
      if (alert.id === id) {
        return {
          ...alert,
          status: alert.status === 'active' ? 'paused' : alert.status === 'paused' ? 'active' : alert.status,
        };
      }
      return alert;
    }));
  };

  const deleteAlert = (id: string) => {
    setAlerts(alerts.filter(a => a.id !== id));
  };

  const getConditionLabel = (condition: AlertCondition) => {
    const type = ALERT_TYPES.find(t => t.id === condition.type);
    switch (condition.type) {
      case 'price_above':
        return `Price > $${condition.value.toLocaleString()}`;
      case 'price_below':
        return `Price < $${condition.value.toLocaleString()}`;
      case 'price_cross':
        return `Price crosses $${condition.value.toLocaleString()}`;
      case 'percent_change':
        return `${condition.value}% move in ${condition.timeframe}`;
      case 'volume_spike':
        return `Volume > ${condition.value}% avg`;
      case 'rsi_above':
        return `RSI > ${condition.value}`;
      case 'rsi_below':
        return `RSI < ${condition.value}`;
      case 'macd_cross':
        return 'MACD crossover';
      default:
        return `${type?.name}: ${condition.value}`;
    }
  };

  const getStatusBadge = (status: Alert['status']) => {
    const configs = {
      active: { color: '#00D4AA', label: 'Active', icon: BellRing },
      triggered: { color: '#F59E0B', label: 'Triggered', icon: Check },
      paused: { color: '#8b949e', label: 'Paused', icon: Pause },
      expired: { color: '#EF4444', label: 'Expired', icon: Clock },
    };
    const config = configs[status];
    const Icon = config.icon;

    return (
      <span 
        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
        style={{ backgroundColor: `${config.color}20`, color: config.color }}
      >
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#080b0f] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Price Alerts</h1>
              <p className="text-sm text-[#8b949e]">Get notified when market conditions are met</p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Alert
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Alerts', value: stats.total, icon: Bell, color: '#8b949e' },
            { label: 'Active', value: stats.active, icon: BellRing, color: '#00D4AA' },
            { label: 'Triggered', value: stats.triggered, icon: Check, color: '#F59E0B' },
            { label: 'Paused', value: stats.paused, icon: Pause, color: '#8b949e' },
          ].map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#8b949e]">{stat.label}</span>
                  <Icon className="h-4 w-4" style={{ color: stat.color }} />
                </div>
                <div className="text-2xl font-bold text-white">{stat.value}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-white/[0.03] rounded-lg p-1">
            {(['all', 'active', 'triggered', 'paused'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium rounded-md transition-all capitalize",
                  filterStatus === status
                    ? "bg-primary text-[#080b0f]"
                    : "text-[#8b949e] hover:text-white"
                )}
              >
                {status}
              </button>
            ))}
          </div>

          <select
            value={filterSymbol}
            onChange={(e) => setFilterSymbol(e.target.value)}
            className="select text-sm"
          >
            <option value="all">All Assets</option>
            {ASSETS.map(asset => (
              <option key={asset} value={asset}>{asset}</option>
            ))}
          </select>
        </div>

        {/* Alerts List */}
        <div className="space-y-3">
          {filteredAlerts.map(alert => {
            const typeConfig = ALERT_TYPES.find(t => t.id === alert.condition.type);
            const Icon = typeConfig?.icon || Target;

            return (
              <div 
                key={alert.id}
                className={cn(
                  "card p-4 transition-all",
                  alert.status === 'paused' && "opacity-60"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${typeConfig?.color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: typeConfig?.color }} />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-white">{alert.name}</span>
                        {getStatusBadge(alert.status)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-primary font-medium">{alert.symbol}</span>
                        <span className="text-[#8b949e]">{getConditionLabel(alert.condition)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Notifications */}
                    <div className="flex items-center gap-2">
                      {alert.notifications.map((n, i) => {
                        const icons = { app: Smartphone, email: Mail, telegram: Globe, webhook: Globe };
                        const NotifIcon = icons[n.type] || Globe;
                        return (
                          <div 
                            key={i}
                            className="p-1.5 rounded bg-white/[0.04]"
                            title={n.type}
                          >
                            <NotifIcon className="h-3.5 w-3.5 text-[#8b949e]" />
                          </div>
                        );
                      })}
                    </div>

                    {/* Trigger count */}
                    <div className="text-right">
                      <div className="text-xs text-[#8b949e]">Triggers</div>
                      <div className="text-sm text-white font-medium">
                        {alert.triggerCount}{alert.maxTriggers ? `/${alert.maxTriggers}` : ''}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleAlertStatus(alert.id)}
                        className={cn(
                          "p-2 rounded-lg transition-colors",
                          alert.status === 'active' 
                            ? "text-[#00D4AA] hover:bg-[#00D4AA]/10" 
                            : "text-[#8b949e] hover:bg-white/[0.06]"
                        )}
                        title={alert.status === 'active' ? 'Pause' : 'Resume'}
                      >
                        {alert.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => deleteAlert(alert.id)}
                        className="p-2 rounded-lg text-[#8b949e] hover:text-[#FF6B6B] hover:bg-[#FF6B6B]/10 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {alert.triggeredAt && (
                  <div className="mt-3 pt-3 border-t border-white/[0.06] text-xs text-[#8b949e]">
                    Last triggered: {new Date(alert.triggeredAt).toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}

          {filteredAlerts.length === 0 && (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 text-[#8b949e]/30 mx-auto mb-4" />
              <p className="text-lg text-[#8b949e] mb-2">No alerts found</p>
              <p className="text-sm text-[#8b949e]/60">Create an alert to get notified about market movements</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Alert Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          
          <div className="relative bg-[#161b22] border border-white/[0.1] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#161b22] p-6 border-b border-white/[0.06]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Create Alert</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm text-[#8b949e] mb-2">Alert Name (optional)</label>
                <input
                  type="text"
                  value={newAlert.name}
                  onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
                  className="input"
                  placeholder="e.g., BTC Breakout Alert"
                />
              </div>

              {/* Asset */}
              <div>
                <label className="block text-sm text-[#8b949e] mb-2">Asset</label>
                <select
                  value={newAlert.symbol}
                  onChange={(e) => setNewAlert({ ...newAlert, symbol: e.target.value })}
                  className="select"
                >
                  {ASSETS.map(asset => (
                    <option key={asset} value={asset}>{asset}</option>
                  ))}
                </select>
              </div>

              {/* Condition Type */}
              <div>
                <label className="block text-sm text-[#8b949e] mb-2">Condition</label>
                <div className="grid grid-cols-2 gap-2">
                  {ALERT_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setNewAlert({ ...newAlert, conditionType: type.id as any })}
                        className={cn(
                          "flex items-center gap-2 p-3 rounded-lg border transition-all text-left",
                          newAlert.conditionType === type.id
                            ? "border-primary bg-primary/5"
                            : "border-white/[0.06] hover:border-white/[0.12] bg-white/[0.02]"
                        )}
                      >
                        <Icon className="h-4 w-4" style={{ color: type.color }} />
                        <span className="text-sm text-white">{type.name}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-[#8b949e]">
                  {ALERT_TYPES.find(t => t.id === newAlert.conditionType)?.description}
                </p>
              </div>

              {/* Value */}
              <div>
                <label className="block text-sm text-[#8b949e] mb-2">
                  {newAlert.conditionType.includes('price') ? 'Price (USDT)' : 
                   newAlert.conditionType.includes('rsi') ? 'RSI Level' :
                   newAlert.conditionType === 'volume_spike' ? 'Volume Threshold (%)' :
                   newAlert.conditionType === 'percent_change' ? 'Percentage (%)' : 'Value'}
                </label>
                <input
                  type="number"
                  value={newAlert.value}
                  onChange={(e) => setNewAlert({ ...newAlert, value: e.target.value })}
                  className="input font-mono"
                  placeholder="0.00"
                />
              </div>

              {/* Timeframe for percent change */}
              {newAlert.conditionType === 'percent_change' && (
                <div>
                  <label className="block text-sm text-[#8b949e] mb-2">Timeframe</label>
                  <select
                    value={newAlert.timeframe}
                    onChange={(e) => setNewAlert({ ...newAlert, timeframe: e.target.value })}
                    className="select"
                  >
                    <option value="5m">5 minutes</option>
                    <option value="15m">15 minutes</option>
                    <option value="1h">1 hour</option>
                    <option value="4h">4 hours</option>
                    <option value="24h">24 hours</option>
                  </select>
                </div>
              )}

              {/* Notifications */}
              <div>
                <label className="block text-sm text-[#8b949e] mb-2">Notifications</label>
                <div className="space-y-2">
                  {[
                    { key: 'app', label: 'In-App', icon: Smartphone },
                    { key: 'email', label: 'Email', icon: Mail },
                    { key: 'telegram', label: 'Telegram', icon: Globe },
                    { key: 'webhook', label: 'Webhook', icon: Globe },
                  ].map(n => {
                    const Icon = n.icon;
                    return (
                      <div 
                        key={n.key}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border transition-all",
                          newAlert.notifications[n.key as keyof typeof newAlert.notifications]
                            ? "border-primary/30 bg-primary/5"
                            : "border-white/[0.06] bg-white/[0.02]"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-4 w-4 text-[#8b949e]" />
                          <span className="text-sm text-white">{n.label}</span>
                        </div>
                        <button
                          onClick={() => setNewAlert({
                            ...newAlert,
                            notifications: {
                              ...newAlert.notifications,
                              [n.key]: !newAlert.notifications[n.key as keyof typeof newAlert.notifications],
                            },
                          })}
                          className={cn(
                            "w-10 h-5 rounded-full transition-all relative",
                            newAlert.notifications[n.key as keyof typeof newAlert.notifications]
                              ? "bg-primary"
                              : "bg-white/[0.1]"
                          )}
                        >
                          <div 
                            className={cn(
                              "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all",
                              newAlert.notifications[n.key as keyof typeof newAlert.notifications]
                                ? "left-5"
                                : "left-0.5"
                            )}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Advanced */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#8b949e] mb-2">Max Triggers (optional)</label>
                  <input
                    type="number"
                    value={newAlert.maxTriggers}
                    onChange={(e) => setNewAlert({ ...newAlert, maxTriggers: e.target.value })}
                    className="input"
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#8b949e] mb-2">Expire After (days)</label>
                  <input
                    type="number"
                    value={newAlert.expireAfter}
                    onChange={(e) => setNewAlert({ ...newAlert, expireAfter: e.target.value })}
                    className="input"
                    placeholder="Never"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#161b22] p-6 border-t border-white/[0.06]">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAlert}
                  disabled={!newAlert.value}
                  className="flex-1 btn-primary"
                >
                  Create Alert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
